import { useEffect, useState } from "react";
import { computeMode, msUntilNextCheck } from "@/lib/dayNight";

/**
 * Watches a profile's day/night settings and returns the current `mode`
 * ("day" | "night"). Also toggles a `.night-mode` class on the <html>
 * element so global CSS variables can re-skin the page without prop
 * drilling.
 *
 * Extracted from Board.jsx as a pure refactor — behavior identical to the
 * inline effects that lived there. The only consumer is the Board, but
 * keeping this as a hook means the Board chrome / Coach / future preview
 * pages can opt in to the same auto-cycle without copying the timer
 * bookkeeping.
 */
export function useDayNightMode(profile) {
  const [mode, setMode] = useState("day");

  useEffect(() => {
    if (!profile) return undefined;
    const tick = () => setMode(computeMode(profile));
    tick();
    let timer = setTimeout(function loop() {
      tick();
      timer = setTimeout(loop, msUntilNextCheck());
    }, msUntilNextCheck());
    return () => clearTimeout(timer);
  }, [profile?.id, profile?.day_start, profile?.night_start, profile?.mode_override]);

  useEffect(() => {
    document.documentElement.classList.toggle("night-mode", mode === "night");
    return () => document.documentElement.classList.remove("night-mode");
  }, [mode]);

  return mode;
}
